import type { SupabaseClient } from '@supabase/supabase-js';

// ============================================================================
// Otto-Lister — Varianten-Attribut-Mapping (Sub-Tab, nur für Variantenväter).
//
// Otto-Produkt = productReference + variations[]; die variantenbildenden Attribute
// (z.B. Farbe, Größe) werden aus Vario gemappt: vario-mapping-sources liefert die
// Varianten-Attribute (isVariantAttribute), der User mappt sie auf Otto-Attributnamen
// + markiert die variantenbildenden (Diskriminatoren). Gespeichert in profile.variants[]
// via otto_save_mapping_profile (Felder bleiben erhalten — Merge).
// ============================================================================

export interface VariantsTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  registerSaver?: (id: string, fn: () => Promise<void>) => void;
}

export interface VariantsTabArgs {
  ottoCategory: string | null;
  varioArticleId: string;
}

interface SourceDef { sourceKey: string; path: string; dataType: string; label: string; isVariantAttribute?: boolean }
interface SourceGroup { key: string; label: string; sources: SourceDef[] }
interface ProfileVariant {
  otto_attribute_name: string; vario_variant_attribute_name: string; is_discriminator: boolean;
}

export async function renderVariantsTab(host: HTMLElement, ctx: VariantsTabCtx, args: VariantsTabArgs): Promise<void> {
  if (!args.ottoCategory) {
    host.innerHTML = banner('warn', 'Keine OTTO-Kategorie gesetzt — Varianten-Mapping ist kategoriegebunden.');
    return;
  }
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Varianten…</div>`;

  let variantAttrs: SourceDef[];
  let existing: Map<string, ProfileVariant>;
  let axisFetchFailed = false;
  try {
    const [sources, profile] = await Promise.all([
      callFn<{ groups: SourceGroup[]; meta?: { variantAxisFetchFailed?: boolean } }>(ctx, 'vario-mapping-sources', { action: 'list', articleId: args.varioArticleId }),
      ctx.supabase.rpc('otto_get_mapping_profile', { _otto_category: args.ottoCategory }),
    ]);
    variantAttrs = (sources.groups ?? []).flatMap((g) => g.sources).filter((s) => s.isVariantAttribute);
    // Signal aus vario-mapping-sources: true = die Varianten-Achsen (/combinations) konnten transient
    // nicht geladen werden. Dann sind 0 Achsen KEIN „kein Variantenvater", sondern ein Ladefehler.
    axisFetchFailed = sources.meta?.variantAxisFetchFailed === true;
    const prof = (profile.data ?? null) as { variants?: ProfileVariant[] } | null;
    existing = new Map((prof?.variants ?? []).map((v) => [v.vario_variant_attribute_name, v]));
  } catch (e) {
    host.innerHTML = banner('error', `Varianten nicht ladbar: ${(e as Error).message}`);
    return;
  }

  if (variantAttrs.length === 0) {
    host.innerHTML = axisFetchFailed
      ? banner('warn', 'Die Varianten-Attribute konnten gerade nicht aus Vario geladen werden (vorübergehender Fehler). Bitte die Ansicht neu laden.')
      : banner('info', 'Für diesen Artikel liefert Vario keine variantenbildenden Attribute. (Ist es wirklich ein Variantenvater mit gepflegten Variantenattributen?)');
    return;
  }

  host.innerHTML = `
    <div class="otto-vattr-pane">
      <div class="otto-vattr-head">
        <div class="kl-sec-lab" style="margin:0;">Varianten-Attribute · ${escapeHtml(args.ottoCategory)}</div>
      </div>
      <p class="otto-vattr-sub">Ordne jedes Vario-Variantenattribut einem OTTO-Attributnamen zu. „Variantenbildend" markiert die Attribute, über die OTTO die Variations unterscheidet.</p>
      <div id="otto-var-rows"></div>
    </div>`;

  const rowsHost = host.querySelector<HTMLDivElement>('#otto-var-rows')!;
  rowsHost.innerHTML = variantAttrs.map((a) => {
    const ex = existing.get(a.label);
    const ottoName = ex?.otto_attribute_name ?? a.label;
    const disc = ex ? ex.is_discriminator : true;
    return `
      <div class="kl-vattr" data-vario="${escapeAttr(a.label)}">
        <div><div class="kl-vattr-l">${escapeHtml(a.label)}</div><div class="kl-vattr-c">Vario-Variantenattribut</div></div>
        <input class="kl-vattr-inp" data-otto-name placeholder="OTTO-Attributname" value="${escapeAttr(ottoName)}" />
        <label class="kl-vattr-cb"><input type="checkbox" data-disc ${disc ? 'checked' : ''}/> variantenbildend</label>
      </div>`;
  }).join('');

  // Varianten-Attribut-Mapping speichern (liest DOM erst zur Ausführungszeit) — wird über die
  // Save-Registry des Detail-Panes vom globalen „Speichern"-Button ausgeführt (kein eigener Button).
  const save = async (): Promise<void> => {
    const variants: unknown[] = [];
    rowsHost.querySelectorAll<HTMLElement>('.kl-vattr').forEach((row) => {
      const vario = row.dataset['vario']!;
      const ottoName = row.querySelector<HTMLInputElement>('[data-otto-name]')?.value.trim() ?? '';
      const disc = row.querySelector<HTMLInputElement>('[data-disc]')?.checked ?? true;
      if (!ottoName) return;
      variants.push({ otto_attribute_name: ottoName, vario_variant_attribute_name: vario, is_discriminator: disc, values: [] });
    });
    // Felder erhalten (Save ersetzt das ganze Profil).
    const { data: cur } = await ctx.supabase.rpc('otto_get_mapping_profile', { _otto_category: args.ottoCategory });
    const fields = ((cur as { fields?: unknown[] } | null)?.fields ?? []).map(toFieldSaveShape);
    const { error } = await ctx.supabase.rpc('otto_save_mapping_profile', {
      _otto_category: args.ottoCategory, _fields: fields, _variants: variants,
    });
    if (error) throw new Error(error.message);
  };
  ctx.registerSaver?.('variant-attrs', save);
}

function toFieldSaveShape(f: unknown): unknown {
  const o = f as Record<string, unknown>;
  return {
    otto_field_key: o['otto_field_key'], field_group: o['field_group'], source_type: o['source_type'],
    vario_path: o['vario_path'], fixed_value: o['fixed_value'], template: o['template'], fallback: o['fallback'],
  };
}

async function callFn<T>(ctx: VariantsTabCtx, name: string, body: unknown): Promise<T> {
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`${name} ${res.status}: ${t.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

function banner(kind: 'warn' | 'error' | 'info', msg: string): string {
  const cls = kind === 'error' ? 'v-banner-danger' : kind === 'warn' ? 'v-banner-warn' : 'v-banner-info';
  const icon = kind === 'error' ? 'fa-circle-xmark' : kind === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-info';
  return `<div class="v-pa-md"><div class="v-banner ${cls}"><span class="v-icon fa-light ${icon}"></span><div>${escapeHtml(msg)}</div></div></div>`;
}
function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}
function escapeAttr(s: string): string { return escapeHtml(s); }
